/** Session 8 Block 2 · Tenant subscription page.
 *  Shows current plan + usage bars + upgrade CTA. */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Sparkles, CheckCircle2, XCircle } from "lucide-react";


function usageBar(current, cap) {
  if (cap === -1) return { pct: 0, label: `${current} · unbegrenzt` };
  const pct = cap > 0 ? Math.min(100, Math.round(current / cap * 100)) : 0;
  return { pct, label: `${current} / ${cap}` };
}


function StatusPill({ status }) {
  if (status === "exceeded")
    return <Badge className="bg-red-100 text-red-800 border-0">
      erreicht</Badge>;
  if (status === "near")
    return <Badge className="bg-amber-100 text-amber-800 border-0">
      knapp</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800 border-0">
    ok</Badge>;
}


export default function TenantSubscription() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [requesting, setRequesting] = useState(null);

  const load = () => {
    Promise.all([
      api.get("/subscription/current"),
      api.get("/subscription/plans"),
    ]).then(([c, p]) => {
      setData(c.data); setPlans(p.data.plans || []);
    }).catch(() => toast.error("Abo-Daten konnten nicht geladen werden."));
  };
  useEffect(load, []);

  const upgrade = async (planId) => {
    setRequesting(planId);
    try {
      await api.post("/subscription/upgrade-request",
                    { target_plan_id: planId });
      toast.success("Upgrade-Anfrage gesendet.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "upgrade_failed");
    } finally { setRequesting(null); }
  };

  if (!data) return null;
  const plan = data.plan; const usage = data.usage;
  const status = data.limits_status;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6"
          data-testid="tenant-subscription-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Dein Abo</h1>
          <p className="text-sm text-muted-foreground">
            Aktueller Plan + Nutzung diesen Monat
          </p>
        </div>
        <Badge className="text-sm" data-testid="tenant-plan-badge">
          {plan.name}
        </Badge>
      </div>

      <Card data-testid="tenant-usage-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Nutzung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {[
            ["max_users", "Nutzer"],
            ["max_active_campaigns", "Aktive Kampagnen"],
            ["max_brand_scans_per_month", "Branding-Scans (Monat)"],
            ["monthly_budget_cap_cents", "Budget (Monat)"],
          ].map(([lk, label]) => {
            const cap = plan.limits?.[lk] ?? 0;
            const cur = usage?.[lk] ?? 0;
            const bar = usageBar(cur, cap);
            return (
              <div key={lk}
                    data-testid={`usage-row-${lk}`}>
                <div className="flex items-center justify-between mb-1">
                  <span>{label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {bar.label}
                    </span>
                    <StatusPill status={status?.[lk] || "ok"} />
                  </div>
                </div>
                <Progress value={bar.pct} className="h-2" />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-semibold mb-2">Upgrade</h2>
        <div className="grid gap-3 md:grid-cols-2">
          {plans.filter((p) => p.plan_id !== plan.plan_id).map((p) => (
            <Card key={p.plan_id}
                   data-testid={`plan-upgrade-card-${p.slug}`}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <span>{p.name}</span>
                  <Badge variant="secondary">
                    {p.custom_pricing
                      ? "Custom"
                      : `${(p.price_monthly_cents / 100).toFixed(0)} EUR/Mo`}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs space-y-1">
                  {Object.entries(p.features || {})
                    .filter(([, v]) => v)
                    .slice(0, 5)
                    .map(([k]) => (
                      <div key={k} className="flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                        {k}
                      </div>
                  ))}
                </div>
                <Button size="sm" className="mt-3"
                          onClick={() => upgrade(p.plan_id)}
                          disabled={requesting === p.plan_id}
                          data-testid={`btn-upgrade-${p.slug}`}>
                  {requesting === p.plan_id
                    ? "Sende…" : "Anfrage senden →"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
